import React, { useEffect, useState } from 'react';
import BreadCrumb from 'src/Components/GlobalComponents/BreadCrumb';
import ConfirmationModal from 'src/Components/GlobalComponents/ConfirmationModal';
import SearchFilterInput from 'src/Components/GlobalComponents/SearchFilterInput';
import TableWrapper from 'src/Components/GlobalComponents/TableWrapper';
import { IHomecareCategory, IqueryParams } from 'src/types/GlobalInterfaces';
import { IPaginated } from 'src/types/paginated';
import emtyImg from 'src/assets/images/emty-img.png';
import editIcon from 'src/assets/images/edit-icon.png';
import deleteIcon from 'src/assets/images/delete-icon.png';
import UpsertHomecareCategories from 'src/Components/AdminComponents/UpsertHomecareCategories';
import httpRequest from 'src/helpers/httpRequest';
import qs from "qs";
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { RootState } from '@store/store';
import { canWrite } from "src/helpers/helperFunctions";
interface IResponse {
    documents: IHomecareCategory[];
    paginated: IPaginated;
};

const HomecareCategories: React.FC = () => {
    const { userDetails } = useSelector((store: RootState) => store.appReducer);
    const hasWriteAccess = canWrite(userDetails, "homeServiceManagement", false);

    const [deleteLoader, setDeleteLoader] = useState<boolean>(false);
    const [deleteModal, setDeleteModal] = useState<boolean>(false);
    const [deleteId, setDeleteId] = useState<string>("")
    const [isEdit, setIsEdit] = useState<IHomecareCategory | null>(null);
    const [isUpsert, setIsUpsert] = useState<boolean>(false);
    const [isLoader, setIsLoader] = useState<boolean>(false);
    const [allCategories, setAllCategories] = useState<IHomecareCategory[]>([]);
    const [paginated, setPaginated] = useState<IPaginated>({
        currentPage: 1,
        totalPages: 1,
    });
    const [queryParams, setQueryParams] = useState<IqueryParams>({
        page: 1,
        limit: 10,
        search: "",
        bool: false,
    });

    const handleChangeText = (txt: string) => {
        setQueryParams(prev => ({
            ...prev,
            page: 1,
            search: txt
        }));
    };

    const handleClose = () => {
        setIsEdit(null);
        setIsUpsert(prev => !prev);
    };

    const deleteModalClose = () => {
        setDeleteModal(false);
        setDeleteId("");
    };

    const deleteRequests = async () => {
        setDeleteLoader(true);
        const { res, err } = await httpRequest<{ message: string; }>({ method: "delete", path: `/homecare-category/${deleteId}`, params: {} });
        if (res) {
            toast(res?.message || "Success!", { type: 'success' });
            setQueryParams(prev => ({
                ...prev,
                bool: !prev.bool,
                page: allCategories?.length > 1 ? prev?.page : prev?.page > 1 ? prev?.page - 1 : prev?.page
            }));
            setDeleteModal(false);
            setDeleteId("");
        } else {
            toast(err, { type: 'error' });
        }
        setDeleteLoader(false);
    };

    const handleEdit = (obj: IHomecareCategory) => {
        if (!hasWriteAccess) {
            return
        }
        setIsUpsert(true);
        setIsEdit(obj);
    };

    const handleDelete = (id: string) => {
        if (!hasWriteAccess) {
            return
        }
        setDeleteModal(true);
        setDeleteId(id);
    };

    const handlePageChange = (e: number) => {
        setQueryParams(prev => ({
            ...prev,
            page: e
        }));
    };

    const handleUpdateList = () => {
        setQueryParams(prev => ({
            ...prev,
            bool: !prev.bool,
        }));
        setIsUpsert(false);
    };

    useEffect(() => {
        (async () => {
            setIsLoader(true);
            let { bool, ...remainingParams } = queryParams;
            const { res, err } = await httpRequest<IResponse>({ path: `/homecare-category/all-paginated?${qs.stringify(remainingParams)}` });
            if (res) {
                setAllCategories(res?.documents);
                setPaginated(res?.paginated);
            } else {
                toast(err, { type: 'error' });
            }
            setIsLoader(false);
        })();
    }, [queryParams]);

    return (
        <>
            <ConfirmationModal
                show={deleteModal}
                onClose={deleteModalClose}
                title="Are you sure you want to delete?"
                leftBtnText="No"
                rightBtnText="Yes"
                onConfirm={deleteRequests}
                loader={deleteLoader}
            />
            <BreadCrumb
                current="At-home Categories"
                previous="dashboard"
                navigateUrl="/admin"
                btnText={"Add Category"}
                btnShow={true}
                onPress={() => {
                    if (!hasWriteAccess) {
                        return
                    }
                    handleClose()
                }}
                isUpsert={isUpsert}
                btnClassName={`${!hasWriteAccess ? 'cursor-not-allowed' : ""}`}
                BtnId='add-category-btn'
            />
            <div className="table-container">
                {isUpsert ?
                    <UpsertHomecareCategories
                        onSuccess={handleUpdateList}
                        isEdit={isEdit}
                    />
                    :
                    <>
                        <SearchFilterInput
                            onChangeText={handleChangeText}
                        />
                        <TableWrapper
                            isLoading={isLoader}
                            thead={["S.No", "Category Name", "Category Image", "Action"]}
                            page={paginated?.currentPage || 1}
                            total={paginated?.totalPages || 0}
                            columnWidth="10% 40% 40% 10%"
                            onChange={handlePageChange}
                        >
                            <div className='tbody-content-height'>
                                {allCategories?.map((item: IHomecareCategory, index: number) => (
                                    <ul key={index} className='font-size-16px tbody-light-table font-Poppins-Regular grid' style={{ gridTemplateColumns: "10% 40% 40% 10%" }}>
                                        <li>{(paginated?.currentPage - 1) * queryParams.limit + (index + 1)}</li>
                                        <li>{item?.categoryName || ""}</li>
                                        <li className="action-last-colum-img">
                                            <img
                                                src={`${item?.categoryImage}`}
                                                alt="category"
                                                onError={(e) => {
                                                    const target = e.target as HTMLImageElement; // Type assertion
                                                    target.src = emtyImg; // Handle error by setting default image
                                                }}
                                            />
                                        </li>
                                        <li className='action-last-colum'>
                                            <img id={`edit-icon-${index + 1}`} onClick={() => handleEdit(item)} src={editIcon} alt="" className={`${!hasWriteAccess ? 'read_access' : ""}`} />
                                            <img id={`delete-icon-${index + 1}`} onClick={() => handleDelete(item?._id)} src={deleteIcon} alt="" className={`${!hasWriteAccess ? 'read_access' : ""}`} />
                                        </li>
                                    </ul>
                                ))}
                            </div>
                        </TableWrapper>
                    </>
                }
            </div>
        </>
    );
};

export default HomecareCategories;